// src/pages/Gallery.jsx
import React, { useEffect, useRef, useState, memo, useMemo } from "react";

/* ----------------------- Utilities ----------------------- */

function useInView(options = { threshold: 0.5, root: null, rootMargin: "0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting);
    }, options);
    io.observe(el);
    return () => io.disconnect();
  }, [options]);

  return { ref, inView };
}

function extractYouTubeId(link) {
  try {
    const u = new URL(link);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const parts = u.pathname.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    return "";
  }
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------- UI Building Blocks -------------------- */

const Shimmer = ({ className = "" }) => (
  <div
    className={cn(
      "relative overflow-hidden bg-slate-200/70",
      "before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.2s_infinite]",
      "before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent",
      className
    )}
    style={{
      // keyframes without a stylesheet:
      // 0% { transform: translateX(-100%) } 100% { transform: translateX(100%) }
      "--tw-translate-x": "-100%",
    }}
  />
);

/* -------------------- YouTube Card (Lazy) ------------------- */

const YouTubeCard = memo(function YouTubeCard({ url, title }) {
  const { ref, inView } = useInView({ threshold: 0.5 });
  const id = useMemo(() => extractYouTubeId(url), [url]);

  // Load the iframe only once the card has been viewed at least once
  const [activated, setActivated] = useState(false);
  useEffect(() => {
    if (inView) setActivated(true);
  }, [inView]);

  const thumb = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  const embed = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&rel=0&modestbranding=1&playsinline=1`;

  const [thumbLoaded, setThumbLoaded] = useState(false);

  return (
    <article ref={ref} className="group">
      {/* Gradient ring frame */}
      <div className="p-[1px] rounded-2xl bg-gradient-to-br from-sky-400/60 via-cyan-400/60 to-indigo-500/60 shadow-[0_10px_30px_-12px_rgba(16,24,40,0.2)]">
        {/* Glassy card */}
        <div className="rounded-2xl bg-white/85 backdrop-blur-sm ring-1 ring-slate-200 overflow-hidden">
          <div className="relative aspect-video">
            {/* Lazy thumbnail (fallback before iframe) */}
            {!activated && (
              <>
                {!thumbLoaded && <Shimmer className="absolute inset-0" />}
                <img
                  src={thumb}
                  alt={title}
                  className={cn(
                    "h-full w-full object-cover transition-opacity duration-300",
                    thumbLoaded ? "opacity-100" : "opacity-0"
                  )}
                  onLoad={() => setThumbLoaded(true)}
                  loading="lazy"
                  decoding="async"
                />
                {/* Play overlay */}
                <div className="pointer-events-none absolute inset-0 grid place-items-center">
                  <div className="grid h-14 w-14 place-items-center rounded-full bg-black/60 text-white shadow-lg ring-1 ring-white/10 transition-transform group-hover:scale-105">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
                {/* Click to open YouTube in a new tab (keeps page light) */}
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0"
                  aria-label={`Open ${title} on YouTube`}
                />
              </>
            )}

            {/* Autoplaying iframe once in view */}
            {activated && (
              <>
                <iframe
                  title={title}
                  src={embed}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {/* Make entire video clickable to open in YouTube in new tab */}
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0"
                  aria-label={`Open ${title} on YouTube`}
                />
              </>
            )}
          </div>

          {/* Meta */}
          <div className="p-4">
            <h3
              className="text-[15px] sm:text-base font-semibold text-slate-900 line-clamp-2"
              title={title}
            >
              {title}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-slate-500">
              Autoplays on scroll • Click to open on YouTube
            </p>
          </div>
        </div>
      </div>
    </article>
  );
});

/* --------------------------- Page --------------------------- */

const Gallery = () => {
  const videos = [
    {
      url: "https://youtu.be/IdUlgjp9Q9Q",
      title:
        "Professional Construction Safety Training & Development | Review | J Prime Buildcon LLP • YouTube #1",
    },
    {
      url: "https://youtu.be/QmjW7BD15CQ",
      title:
        "Professional Construction Safety Training & Development | Review | J Prime Buildcon LLP",
    },
    {
      url: "https://youtu.be/XU_kDacOXqo",
      title:
        "Interview Training & Development Program | Mr. Sahil Shares Experience | Growmax OGCS Training",
    },
    {
      url: "https://youtu.be/oMiCRC_S7Ic",
      title:
        "Interview Training & Development Program | Mr Ibrahim Shares Experience | Growmax OGCS Training",
    },
    {
      url: "https://youtu.be/ynt38ldQDAY?si=idkIhGMDIFP7OI7M",
      title:
        "Professional Construction Safety Training & Development | Review | J Prime Buildcon LLP by Mr. Nikhil Kamble",
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-sky-500 via-cyan-500 to-indigo-600 bg-clip-text text-transparent">
                Gallery
              </span>
            </h1>
            <p className="text-sm text-slate-600">
              Highlights from our programs, trainings, and reviews.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 self-start sm:self-auto rounded-full bg-slate-100 px-3 py-1 ring-1 ring-slate-200 text-xs sm:text-sm text-slate-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {videos.length} video{videos.length > 1 ? "s" : ""}
          </div>
        </div>
      </header>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {videos.map((v, i) => (
          <YouTubeCard key={i} url={v.url} title={v.title} />
        ))}
      </div>
    </section>
  );
};

export default Gallery;










































































// // src/pages/Gallery.jsx
// import React, { useEffect, useRef, useState, memo } from "react";

// // --- in-view hook ---
// function useInView(options = { threshold: 0.5 }) {
//   const ref = useRef(null);
//   const [inView, setInView] = useState(false);

//   useEffect(() => {
//     const el = ref.current;
//     if (!el) return;
//     const io = new IntersectionObserver(([entry]) => {
//       setInView(entry.isIntersecting);
//     }, options);
//     io.observe(el);
//     return () => io.disconnect();
//   }, [options]);

//   return { ref, inView };
// }

// function extractYouTubeId(link) {
//   try {
//     const u = new URL(link);
//     if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
//     if (u.searchParams.get("v")) return u.searchParams.get("v");
//     const parts = u.pathname.split("/");
//     return parts[parts.length - 1] || "";
//   } catch {
//     return "";
//   }
// }

// const YouTubeCard = memo(function YouTubeCard({ url, title }) {
//   const { ref, inView } = useInView();
//   const id = extractYouTubeId(url);
//   const base = `https://www.youtube.com/embed/${id}`;
//   const src = `${base}?autoplay=${inView ? 1 : 0}&mute=1&loop=1&playlist=${id}&controls=1&rel=0&modestbranding=1&playsinline=1`;

//   return (
//     <article
//       ref={ref}
//       className="group relative rounded-2xl overflow-hidden shadow-sm ring-1 ring-gray-200/70 hover:shadow-xl hover:ring-gray-300 transition-all bg-white"
//     >
//       {/* Click-overlay opens YouTube */}
//       <a
//         href={url}
//         target="_blank"
//         rel="noopener noreferrer"
//         className="absolute inset-0 z-10"
//         aria-label={`Open ${title} on YouTube`}
//       />
//       <div className="relative aspect-video overflow-hidden bg-gray-100">
//         <iframe
//           title={title}
//           src={src}
//           allow="autoplay; encrypted-media; picture-in-picture"
//           allowFullScreen
//           className="h-full w-full"
//         />
//         <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
//       </div>
//       <div className="p-4">
//         <h3 className="text-base font-semibold text-gray-900">{title}</h3>
//         <p className="text-sm text-gray-500">Auto-plays in view • Click to open on YouTube</p>
//       </div>
//     </article>
//   );
// });

// const Gallery = () => {
//   const videos = [
//     { url: "https://youtu.be/IdUlgjp9Q9Q", title: "Professional Construction Safety Training & Development| Review |J Prime Buildcon LLPYouTube #1" },
//     { url: "https://youtu.be/QmjW7BD15CQ", title: "Professional Construction Safety Training & Development| Review |J Prime Buildcon LLP " },
//     { url: "https://youtu.be/XU_kDacOXqo", title: "Interview Training & Development Program | Mr. Sahil Shares Experience | Growmax OGCS Training" },
//     { url: "https://youtu.be/oMiCRC_S7Ic", title: "Interview Training & Development Program | Mr Ibrahim Shares Experience | Growmax OGCS Training" },
//     {url:"https://youtu.be/ynt38ldQDAY?si=idkIhGMDIFP7OI7M",title:"Professional Construction Safety Training & Development| Review |J Prime Buildcon LLP by Mr. Nikhil Kamble"}
//   ];

//   return (
//     <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
//       <header className="mb-8">
        
//       </header>

//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//         {videos.map((v, i) => (
//           <YouTubeCard key={i} url={v.url} title={v.title} />
//         ))}
//       </div>
//     </section>
//   );
// };

// export default Gallery;
